import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import MaterialIcons from '@react-native-vector-icons/material-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import Feather from '@react-native-vector-icons/feather/static';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import Foundation from '@react-native-vector-icons/foundation/static';
import { useDispatch } from 'react-redux';

import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import useLocation from '../../hooks/useLocation';
import useShowServiceDisplay from '../../hooks/useShowServiceDisplay';
import { clearEmployeeAndBusinessInfo } from '../../redux/AuthSlice';
import { logoutAction } from '../../redux/Store';
import { selectCartTotalQuantity } from '../../redux/cartSlice';
import { useAppSelector } from '../../redux/helper';
import { useTheme, GTFonts, withOpacity } from '../../theme';
import { WEB_NAV_SIDEBAR_RAIL_S } from '../../theme/webLayoutWidths';
import { borderWidth, fs, iconSize, s, v, dumpTokens } from '../../utils/libs';
import { AppSnackbar } from '../../services/AppSnackbar';

type IconLibrary =
  | 'MaterialCommunityIcons'
  | 'MaterialIcons'
  | 'FontAwesome6'
  | 'Feather'
  | 'Ionicons'
  | 'Foundation';

type SideNavItemProps = {
  label: string;
  icon: string;
  iconLibrary?: IconLibrary;
  isActive: boolean;
  onPress: () => void;
  badge?: number;
  styles: any;
  theme: any;
};

const SideNavItem = React.memo(
  ({
    label,
    icon,
    iconLibrary = 'MaterialCommunityIcons',
    isActive,
    onPress,
    badge,
    styles,
    theme,
  }: SideNavItemProps) => {
    const [isHovered, setIsHovered] = useState(false);

    const IconComponent =
      iconLibrary === 'FontAwesome6'
        ? FontAwesome6
        : iconLibrary === 'Feather'
          ? Feather
          : iconLibrary === 'Ionicons'
            ? Ionicons
            : iconLibrary === 'Foundation'
              ? Foundation
              : iconLibrary === 'MaterialIcons'
                ? MaterialIcons
                : MaterialCommunityIcons;

    const iconColor = isActive
      ? theme.colors.primaryDark
      : isHovered
        ? theme.colors.primary
        : theme.colors.textGray600;

    return (
      <Pressable
        onPress={onPress}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        style={[
          styles.sideNavItem,
          isActive && styles.sideNavItemActive,
          isHovered && !isActive && styles.sideNavItemHovered,
        ]}>
        <View style={styles.sideNavIconWrapper}>
          <IconComponent
            name={icon as any}
            {...(iconLibrary === 'FontAwesome6'
              ? { iconStyle: 'solid' as const }
              : null)}
            size={iconSize(icon === 'basket-shopping' ? 20 : 22)}
            color={iconColor}
          />
          {badge != null && badge > 0 && (
            <View style={styles.sideNavBadge}>
              <Text style={styles.sideNavBadgeText}>
                {badge > 99 ? '99+' : badge}
              </Text>
            </View>
          )}
        </View>
        <Text
          style={[
            styles.sideNavItemText,
            isActive && styles.sideNavItemTextActive,
            isHovered && !isActive && styles.sideNavItemTextHovered,
          ]}>
          {label}
        </Text>
      </Pressable>
    );
  },
);

function getActiveRouteName(state: any): string {
  if (!state || !Array.isArray(state.routes) || typeof state.index !== 'number')
    return '';
  const route = state.routes[state.index];
  if (!route) return '';
  if (route.state) return getActiveRouteName(route.state);
  return String(route.name || '');
}

type WebSidebarProps = {
  onOpenCalculator?: () => void;
  isDemoMode?: boolean;
  cartBadgeOverride?: number;
};

export default function WebSidebar({
  onOpenCalculator,
  isDemoMode = false,
  cartBadgeOverride,
}: WebSidebarProps) {
  const navigation = useNavigation<any>();
  const navState = useNavigationState(s => s);

  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const dispatch = useDispatch();
  const axiosprivate = useAxiosPrivate();
  const { auth, setAuth } = useAuth();
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  const { isAdmin } = useLocation();
  const { showservicedisplay } = useShowServiceDisplay();
  const cartTotalQuantity = useAppSelector(selectCartTotalQuantity);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const hideProductsBasketOrders = !isDemoMode && showservicedisplay;

  const activeLeafRoute = useMemo(
    () => getActiveRouteName(navState),
    [navState],
  );

  const displayCartBadge = isDemoMode
    ? (cartBadgeOverride ?? 0)
    : cartTotalQuantity;

  const activeNavId = useMemo(() => {
    if (isDemoMode) {
      switch (activeLeafRoute) {
        case 'DemoProducts':
          return 'products';
        case 'DemoCart':
          return 'basket';
        case 'DemoCheckout':
          return 'orders';
        default:
          return '';
      }
    }
    switch (activeLeafRoute) {
      case 'ProductsScreen':
        return 'products';
      case 'Cart':
        return 'basket';
      case 'Orders':
        return 'orders';
      case 'Notes':
        return 'notes';
      case 'StatsSummaryScreen':
        return 'report';
      case 'SettingsScreen':
        return 'settings';
      case 'Calculator':
        return 'calculator';
      case 'Profile':
        return 'profile';
      case 'BackOffice':
        return 'backoffice';
      default:
        return '';
    }
  }, [activeLeafRoute, isDemoMode]);

  const goToTabOrMain = useCallback(
    (tabName: string, tabParams?: any) => {
      try {
        const state = (navigation as any)?.getState?.();
        const routeNames: string[] = state?.routeNames || [];
        const isTabNavigator =
          Array.isArray(routeNames) &&
          routeNames.includes('ProductsScreen') &&
          routeNames.includes('Cart') &&
          routeNames.includes('Orders');

        if (isTabNavigator) {
          (navigation as any).navigate(tabName, tabParams);
          return;
        }

        // Stack context (e.g. Settings/Calculator): jump back into MainScreen tabs
        (navigation as any).navigate(
          'MainScreen',
          tabParams
            ? { screen: tabName, params: tabParams }
            : { screen: tabName },
        );
      } catch {
        // last-resort fallback
        (navigation as any).navigate(tabName, tabParams);
      }
    },
    [navigation],
  );

  const handleBackOfficeNav = useCallback(async () => {
    try {
      navigation.navigate('BackOffice');
      await autoLoginOwner();
    } catch {}
  }, [navigation, autoLoginOwner]);

  const clearAll = useCallback(async () => {
    await dumpTokens();
    setAuth({ accessToken: '', refreshToken: '' });
    dispatch(clearEmployeeAndBusinessInfo());
    dispatch(logoutAction());
  }, [dispatch, setAuth]);

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      const refreshToken = await AsyncStorage.getItem('refreshToken');
      await clearAll();
      await axiosprivate.post(
        `bp_employee_log_out_information/${refreshToken}`,
        {},
        { headers: { Authorization: 'Bearer ' + auth.accessToken } },
      );
    } catch {
      // ignore
    } finally {
      setIsLoggingOut(false);
    }
  }, [axiosprivate, auth.accessToken, clearAll, isLoggingOut]);

  return (
    <>
      <View style={styles.sidebar}>
        <ScrollView
          style={styles.sidebarNav}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.sidebarNavContent}>
          {!hideProductsBasketOrders && (
            <SideNavItem
              label="Products"
              icon="format-list-bulleted"
              iconLibrary="MaterialIcons"
              isActive={activeNavId === 'products'}
              onPress={() =>
                isDemoMode
                  ? navigation.replace('DemoProducts')
                  : goToTabOrMain('ProductsScreen', { from: 'Sidebar' })
              }
              styles={styles}
              theme={theme}
            />
          )}

          {!hideProductsBasketOrders && (
            <SideNavItem
              label="Basket"
              icon="basket-shopping"
              iconLibrary="FontAwesome6"
              isActive={activeNavId === 'basket'}
              onPress={() =>
                isDemoMode
                  ? navigation.replace('DemoCart')
                  : goToTabOrMain('Cart')
              }
              styles={styles}
              theme={theme}
              badge={displayCartBadge}
            />
          )}

          {!hideProductsBasketOrders && (
            <SideNavItem
              label="Orders"
              icon="receipt-sharp"
              iconLibrary="Ionicons"
              isActive={activeNavId === 'orders'}
              onPress={() =>
                isDemoMode
                  ? AppSnackbar.show(
                      'Orders is not available in demo mode. Create a BizPlus POS account to use this feature.',
                      'info',
                    )
                  : goToTabOrMain('Orders')
              }
              styles={styles}
              theme={theme}
            />
          )}

          {isAdmin && !isDemoMode ? (
            <SideNavItem
              label="Stats"
              icon="graph-pie"
              iconLibrary="Foundation"
              isActive={activeNavId === 'report'}
              onPress={() => goToTabOrMain('StatsSummaryScreen')}
              styles={styles}
              theme={theme}
            />
          ) : null}

          {Platform.OS === 'web' ? (
            <SideNavItem
              label="Notes"
              icon="note-text-outline"
              isActive={activeNavId === 'notes'}
              onPress={() =>
                isDemoMode
                  ? AppSnackbar.show(
                      'Notes is not available in demo mode. Create a BizPlus POS account to use this feature.',
                      'info',
                    )
                  : navigation.navigate('Notes')
              }
              styles={styles}
              theme={theme}
            />
          ) : null}

          <View style={styles.sidebarDivider} />

          <SideNavItem
            label="Calculator"
            icon="calculator"
            iconLibrary="Ionicons"
            isActive={activeNavId === 'calculator'}
            onPress={() => onOpenCalculator?.()}
            styles={styles}
            theme={theme}
          />

          <SideNavItem
            label="Settings"
            icon="cog"
            isActive={activeNavId === 'settings'}
            onPress={() =>
              isDemoMode
                ? AppSnackbar.show(
                    'Settings is not available in demo mode. Create a BizPlus POS account to use this feature.',
                    'info',
                  )
                : navigation.navigate('SettingsScreen')
            }
            styles={styles}
            theme={theme}
          />

          {!isAdmin && !isDemoMode ? (
            <SideNavItem
              label="Profile"
              icon="person"
              iconLibrary="Ionicons"
              isActive={activeNavId === 'profile'}
              onPress={() => goToTabOrMain('Profile')}
              styles={styles}
              theme={theme}
            />
          ) : null}
        </ScrollView>

        <View style={styles.sidebarFooter}>
          {isAdmin && !isDemoMode ? (
            <SideNavItem
              label="Back Office"
              icon="office-building"
              isActive={activeNavId === 'backoffice'}
              onPress={() => void handleBackOfficeNav()}
              styles={styles}
              theme={theme}
            />
          ) : null}

          {isDemoMode ? (
            <SideNavItem
              label="Back Office"
              icon="office-building"
              isActive={false}
              onPress={() =>
                AppSnackbar.show(
                  'Back office is not available in demo mode. Create a BizPlus POS account to use this feature.',
                  'info',
                )
              }
              styles={styles}
              theme={theme}
            />
          ) : null}

          {!isDemoMode ? (
            <Pressable
              onPress={handleLogout}
              disabled={isLoggingOut}
              style={({ hovered }: any) => [
                styles.logoutButton,
                hovered && !isLoggingOut && styles.logoutButtonHovered,
                isLoggingOut && styles.logoutButtonLoading,
              ]}>
              {isLoggingOut ? (
                <ActivityIndicator size="small" color={theme.colors.error} />
              ) : (
                <Ionicons
                  name="log-out-outline"
                  size={iconSize(18)}
                  color={theme.colors.error}
                />
              )}
              <Text style={styles.logoutButtonText}>
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>
    </>
  );
}

const createStyles = (theme: any) => {
  const colors = theme.colors;
  const sidebarWidth = s(WEB_NAV_SIDEBAR_RAIL_S);

  return StyleSheet.create({
    sidebar: {
      width: sidebarWidth,
      minWidth: sidebarWidth,
      maxWidth: sidebarWidth,
      flexShrink: 0,
      flexGrow: 0,
      overflow: 'hidden',
      backgroundColor: colors.surface,
      flexDirection: 'column',
    },
    sidebarNav: {
      flex: 1,
      borderRightWidth: borderWidth(1),
      borderRightColor: colors.border,
    },
    sidebarNavContent: {
      paddingTop: v(8),
      paddingBottom: v(8),
      paddingHorizontal: s(8),
      gap: v(2),
    },
    sidebarDivider: {
      height: borderWidth(1),
      backgroundColor: colors.border,
      marginVertical: v(8),
      marginHorizontal: s(12),
    },
    sideNavItem: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: v(4),
      paddingVertical: v(10),
      paddingHorizontal: s(4),
      borderRadius: theme.borderRadius.md,
      minHeight: v(64),
    },
    sideNavItemActive: {
      backgroundColor: withOpacity(colors.primaryDark, 0.12),
    },
    sideNavItemHovered: {
      backgroundColor: colors.surfaceSecondary,
    },
    sideNavIconWrapper: {
      position: 'relative',
      width: s(28),
      height: s(28),
      alignItems: 'center',
      justifyContent: 'center',
    },
    sideNavBadge: {
      position: 'absolute',
      top: -v(6),
      right: -s(8),
      minWidth: s(18),
      height: s(18),
      borderRadius: theme.borderRadius.round,
      backgroundColor: colors.primaryDark,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: s(4),
    },
    sideNavBadgeText: {
      fontFamily: GTFonts.bold,
      fontSize: fs(10),
      color: colors.surface,
    },
    sideNavItemText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(11),
      color: colors.textGray600,
      textAlign: 'center',
      lineHeight: fs(11),
    },
    sideNavItemTextActive: {
      color: colors.primaryDark,
    },
    sideNavItemTextHovered: {
      color: colors.text,
    },

    sidebarFooter: {
      borderTopWidth: borderWidth(1),
      borderTopColor: colors.border,
      borderRightWidth: borderWidth(1),
      borderRightColor: colors.border,
      padding: s(8),
      alignItems: 'center',
    },
    logoutButton: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: v(4),
      padding: s(8),
      borderRadius: theme.borderRadius.md,
      width: '100%',
    },
    logoutButtonHovered: {
      backgroundColor: withOpacity(colors.error, 0.1),
    },
    logoutButtonLoading: {
      opacity: 0.7,
    },
    logoutButtonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(9),
      color: colors.error,
      textAlign: 'center',
    },
  });
};
